import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import "./css/LanguagePreferences.css";
import ukFlag from "../assets/icons/uk.png";
import uaeFlag from "../assets/icons/uae.png";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;

export default function LanguagePreferences() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");
  const [selectedLanguage, setSelectedLanguage] = useState("en");

  // Translations
  const t = {
    en: {
      generalSettings: "General Settings",
      languagePreferences: "Language Preferences",
      choosePreferred: "Choose your preferred one",
      english: "English",
      arabic: "Arabic",
      save: "Save",
      saving: "Saving...",
      loading: "Loading",
    },
    ar: {
      generalSettings: "الإعدادات العامة",
      languagePreferences: "تفضيلات اللغة",
      choosePreferred: "اختر المفضل لديك",
      english: "الإنجليزية",
      arabic: "العربية",
      save: "حفظ",
      saving: "جاري الحفظ...",
      loading: "جاري التحميل",
    },
  }[lang];

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profileData = data.response;
          
          // Set language preference from profile
          if (profileData.language_preference) {
            setSelectedLanguage(profileData.language_preference);
            localStorage.setItem("app_language", profileData.language_preference);
            setLang(profileData.language_preference);
          } else {
            // Default to current language if not set
            const currentLang = localStorage.getItem("app_language") || "en";
            setSelectedLanguage(currentLang);
          }
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        showToast(err.message || "Failed to load profile", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle save
  const handleSave = async () => {
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append("language_preference", selectedLanguage);

      const response = await fetch(
        `${USER_BASE_URL}/${VERSION}/profile/edit`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "app-version": "1.0.0",
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (data && data.response_code === 200) {
        // Update localStorage
        localStorage.setItem("app_language", selectedLanguage);
        setLang(selectedLanguage);
        
        showToast(
          lang === "ar" ? "تم الحفظ بنجاح" : "Saved successfully",
          "success"
        );
        // Refresh page
        window.location.reload();
      } else {
        showToast(
          data?.message || (lang === "ar" ? "فشل الحفظ" : "Failed to save"),
          "error"
        );
        setSaving(false);
      }
    } catch (err) {
      console.error("Error saving language preference:", err);
      showToast(
        err.message || (lang === "ar" ? "فشل الحفظ" : "Failed to save"),
        "error"
      );
      setSaving(false);
    }
  };

  // Loading screen
  if (loading) {
    return (
      <div className="language-preferences-loading">
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  return (
    <div className="language-preferences-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />

      {/* Page Header with Back Button */}
      <div className="language-preferences-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back"></i>
        </button>
        <h1 className="language-preferences-page-title">{t.generalSettings}</h1>
        <div className="header-spacer"></div>
      </div>

      {/* Main Content */}
      <div className="language-preferences-content">
        {/* Section Heading */}
        <h2 className="language-preferences-heading">{t.languagePreferences}</h2>
        <p className="language-preferences-subtitle">{t.choosePreferred}</p>

        {/* Language Options */}
        <div className="language-options">
          {/* English Option */}
          <div
            className={`language-option-box ${selectedLanguage === "en" ? "active" : ""}`}
            onClick={() => !saving && setSelectedLanguage("en")}
          >
            <div className="language-option-content">
              <input
                type="radio"
                name="language"
                checked={selectedLanguage === "en"}
                readOnly
                className="language-radio"
              />
              <img src={ukFlag} alt="English" className="language-flag" />
              <span className="language-name">{t.english}</span>
            </div>
          </div>

          {/* Arabic Option */}
          <div
            className={`language-option-box ${selectedLanguage === "ar" ? "active" : ""}`}
            onClick={() => !saving && setSelectedLanguage("ar")}
          >
            <div className="language-option-content">
              <input
                type="radio"
                name="language"
                checked={selectedLanguage === "ar"}
                readOnly
                className="language-radio"
              />
              <img src={uaeFlag} alt="Arabic" className="language-flag" />
              <span className="language-name">{t.arabic}</span>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button
          className="language-preferences-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="save-spinner"></span>
              {t.saving}
            </>
          ) : (
            t.save
          )}
        </button>
      </div>

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="settings" lang={lang} />
    </div>
  );
}

